// Design System v3 — "ProRentz Premium" (Apple-HIG-inspired redesign, 2026-07)
// Direction: calm, spacious, native-iOS feel. Coral primary actions (per master
// UI spec) + indigo accents; whitespace over dividers; one gradient per screen.
// Surface hierarchy: bg (deepest) → sf (surface) → cd (card) → el (elevated)
// Text hierarchy: tx (primary) → ts (secondary text) → tt (tertiary/muted)
//
// NOTE: keys are consumed app-wide as `h.*` via AppContext — never remove one.
const themes = {
    dark: {
        dk: true,             // Mode flag — used by style helpers (e.g. inpS) so
                              // CSS color-scheme can match, fixing white-on-white
                              // OS-native dropdown menus in dark mode.
        bg: "#0a0a0c",       // Deep rich black (not pure)
        sf: "#121216",       // Surface layer
        cd: "#1a1a20",       // Card
        el: "#232329",       // Elevated surface
        tx: "#f5f5f7",       // Primary text (soft white)
        ts: "#a1a1aa",       // Secondary text
        tt: "#6e6e78",       // Tertiary/muted text
        sc: "#8e8e93",       // Secondary (kept for compat)
        ac: "#ff6b7e",       // Primary action — coral (lifted for dark contrast)
        a2: "#ff8494",       // Coral hover/lighter (dark mode lightens on hover)
        in: "#818cf8",       // Indigo accent (links, highlights, hero)
        gn: "#22c55e",       // Green — success
        bd: "rgba(255,255,255,0.08)", // Subtle border
        b2: "rgba(255,255,255,0.13)", // Stronger border
        rd: "#f87171",       // Red — error
        wn: "#fbbf24",       // Warning amber
        ib: "rgba(255,255,255,0.06)", // Input bg
        pp: "#a855f7",       // Purple (kept for compat)
        gl: "linear-gradient(135deg, #ff7285 0%, #f43f5e 100%)", // Primary (coral) gradient
        g2: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)", // Hero (indigo) gradient
        sh: "0 6px 18px rgba(0,0,0,0.35)",                        // Card shadow (soft)
        s2: "0 12px 28px rgba(0,0,0,0.45)",                       // Elevated shadow
        s3: "0 20px 48px rgba(0,0,0,0.55)",                       // Modal shadow
        ov: "rgba(0,0,0,0.6)" // Overlay
    },
    light: {
        dk: false,
        bg: "#f7f8fa",       // Spec background — near-white cool gray
        sf: "#f1f2f6",       // Surface layer
        cd: "#ffffff",       // Card — pure white
        el: "#ffffff",       // Elevated surface
        tx: "#111827",       // Primary text (spec)
        ts: "#6b7280",       // Secondary text (spec)
        tt: "#9ca3af",       // Tertiary/muted text
        sc: "#8e8e93",       // Secondary (kept for compat)
        ac: "#ff5a6e",       // Primary action — coral (spec, softened)
        a2: "#f43f5e",       // Coral darker (hover, small coral text needs this)
        in: "#4f46e5",       // Indigo accent (links, highlights, hero)
        gn: "#16a34a",       // Green — success (legible on white; fills may use #22c55e)
        bd: "#e5e7eb",       // Border (spec) — prefer whitespace over borders
        b2: "#d1d5db",       // Stronger border
        rd: "#ef4444",       // Red — error
        wn: "#d97706",       // Warning amber (legible); fills may use #f59e0b
        ib: "rgba(17,24,39,0.04)", // Input bg
        pp: "#9333ea",       // Purple (kept for compat)
        gl: "linear-gradient(135deg, #ff7285 0%, #f43f5e 100%)", // Primary (coral) gradient
        g2: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)", // Hero (indigo) gradient
        sh: "0 6px 18px rgba(17,24,39,0.06)",                     // Card shadow — spec: blur 18 @ 6%
        s2: "0 12px 28px rgba(17,24,39,0.08)",                    // Elevated shadow
        s3: "0 20px 48px rgba(17,24,39,0.14)",                    // Modal shadow
        ov: "rgba(17,24,39,0.4)" // Overlay
    }
};

// ─── Spec tokens (radii · spacing · type · motion) ─────────────────────────
// New pages/redesigned screens import these instead of hardcoding numbers:
//   import themes, { T } from "../../constants/themes";
export const T = {
    font: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    r: { card: 24, btn: 16, input: 16, img: 18, sheet: 30, chip: 999 }, // border radii
    sp: { out: 20, pad: 20, gap: 16, sec: 32 },  // outer margin · card padding · gap · section gap
    fz: { lt: 34, ti: 28, hd: 22, sub: 18, body: 16, cap: 13, tiny: 11 }, // type scale
    mo: { fast: "180ms", base: "240ms", slow: "320ms", ease: "cubic-bezier(0.32, 0.72, 0.22, 1)" }, // motion
    press: { transform: "scale(0.97)" }, // button press affordance
};

export default themes;
